import type {
  PullRequestActor,
  PullRequestAction,
  PullRequestActivity,
  PullRequestComment,
  PullRequestCommit,
  PullRequestDetail,
} from "@cmmd-forge/contracts";

export type PullRequestReviewerVerdict = "approved" | "changes-requested";

export interface PullRequestReviewerEntry {
  readonly key: string;
  readonly actor: PullRequestActor | null;
  readonly verdict: PullRequestReviewerVerdict | null;
  readonly stale: boolean;
}

export function canWritePullRequestDetail(isFresh: boolean, permitted: boolean): boolean {
  return isFresh && permitted;
}

export function keyedPullRequestChecks(checks: PullRequestDetail["checks"]) {
  const occurrences = new Map<string, number>();
  return checks.map((check) => {
    const identity = `${check.name}:${check.status}:${check.description ?? ""}:${check.url ?? ""}`;
    const occurrence = occurrences.get(identity) ?? 0;
    occurrences.set(identity, occurrence + 1);
    return { check, key: `${identity}:${occurrence}` } as const;
  });
}

export function availablePullRequestActions(input: {
  readonly isFresh: boolean;
  readonly capabilities: ReadonlyArray<PullRequestAction>;
  readonly viewerPermissions: ReadonlyArray<PullRequestAction>;
}): ReadonlyArray<PullRequestAction> {
  if (!input.isFresh) return [];
  const permitted = new Set(input.viewerPermissions);
  return input.capabilities.filter((action) => permitted.has(action));
}

export function pullRequestStateLabel(state: "open" | "closed" | "merged", isDraft: boolean) {
  if (state === "merged") return "Merged";
  if (state === "closed") return "Closed";
  return isDraft ? "Draft" : "Open";
}

export function mergeabilityLabel(mergeability: PullRequestDetail["mergeability"]): string {
  if (mergeability === "mergeable") return "Ready to merge";
  if (mergeability === "conflicting") return "Conflicts need resolution";
  return "Mergeability unavailable";
}

export function baseComparisonLabel(
  baseComparison: PullRequestDetail["baseComparison"],
  behindBy: PullRequestDetail["behindBy"],
): string | null {
  if (baseComparison === "up-to-date") return "Up to date with base";
  if (baseComparison === "behind") {
    return behindBy === undefined ? "Behind base" : `${behindBy} commits behind base`;
  }
  return null;
}

export function summaryActivityEnrichment(
  detail: Pick<PullRequestDetail, "author" | "reviewers">,
  activity: Pick<PullRequestActivity, "author" | "reviewers"> | undefined,
) {
  return {
    author: activity?.author ?? detail.author,
    reviewers: uniqueReviewers([...detail.reviewers, ...(activity?.reviewers ?? [])]),
  };
}

function reviewerKey(login: string): string {
  return login.toLowerCase();
}

function uniqueReviewers(
  reviewers: ReadonlyArray<PullRequestActor>,
): ReadonlyArray<PullRequestActor> {
  const seen = new Set<string>();
  return reviewers.filter((reviewer) => {
    const key = reviewerKey(reviewer.login);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function reviewVerdict(state: string | null): PullRequestReviewerVerdict | null {
  switch (state?.trim().toLowerCase().replaceAll("_", "-")) {
    case "approved":
      return "approved";
    case "changes-requested":
      return "changes-requested";
    default:
      return null;
  }
}

function newestCommitAt(commits: ReadonlyArray<PullRequestCommit>): number | null {
  let newest: number | null = null;
  for (const commit of commits) {
    const timestamp = Date.parse(commit.committedDate);
    if (!Number.isNaN(timestamp) && (newest === null || timestamp > newest)) newest = timestamp;
  }
  return newest;
}

/** Requested reviewers first, then people who delivered a verdict without a request. */
export function reviewerEntries(
  reviewers: ReadonlyArray<PullRequestActor>,
  comments: ReadonlyArray<PullRequestComment>,
  commits: ReadonlyArray<PullRequestCommit>,
): ReadonlyArray<PullRequestReviewerEntry> {
  const latest = new Map<
    string,
    {
      readonly comment: PullRequestComment;
      readonly verdict: PullRequestReviewerVerdict;
    }
  >();
  for (const comment of comments) {
    const verdict = reviewVerdict(comment.reviewState);
    if (verdict === null) continue;
    const key = comment.author === null ? `ghost:${comment.id}` : reviewerKey(comment.author.login);
    const previous = latest.get(key);
    if (
      previous !== undefined &&
      Date.parse(previous.comment.createdAt) > Date.parse(comment.createdAt)
    )
      continue;
    latest.set(key, { comment, verdict });
  }

  const latestCommit = newestCommitAt(commits);
  const entryFor = (key: string, actor: PullRequestActor | null): PullRequestReviewerEntry => {
    const review = latest.get(key);
    const reviewAt = review === undefined ? Number.NaN : Date.parse(review.comment.createdAt);
    return {
      key,
      actor,
      verdict: review?.verdict ?? null,
      stale: latestCommit !== null && !Number.isNaN(reviewAt) && reviewAt < latestCommit,
    };
  };
  const requested = uniqueReviewers(reviewers);
  const requestedKeys = new Set(requested.map((reviewer) => reviewerKey(reviewer.login)));
  return [
    ...requested.map((reviewer) => entryFor(reviewerKey(reviewer.login), reviewer)),
    ...[...latest.entries()]
      .filter(([key]) => !requestedKeys.has(key))
      .map(([key, review]) => entryFor(key, review.comment.author)),
  ];
}
